import React, { useState } from 'react';
import { Bell, ArrowLeft, RefreshCw } from 'lucide-react';
import { getProductImage } from '../utils/imageHelpers';
import ProductDetailsModal from './modals/ProductDetailsModal';

const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date.getTime())) return String(d);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${String(date.getDate()).padStart(2,'0')}-${months[date.getMonth()]}-${date.getFullYear()}`;
};

const SubscriptionsScreen = ({
  allSubscriptions = [],
  loading = false,
  loadSubscriptions,
  handlePauseSubscription,
  handleResumeSubscription,
  handleCancelSubscription,
  handleModifySubscription,
  handleReactivateSubscription,
  appTheme = 'blue',
  setActiveTab,
  goBack,
  isDesktop = false,
  addToCart,
  cart = [],
  getCartCount,
  getCartTotal,
  openCheckout,
  showToast,
  toggleFavorite,
  favorites = [],
  setShowSubscription,
}) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductDetails, setShowProductDetails] = useState(false);
  const openDetails = (prod) => {
    if (!prod || (!prod.id && !prod.name)) return;
    setSelectedProduct(prod);
    setShowProductDetails(true);
  };

  const renderCard = (sub) => {
    const statusLower = (sub.status || '').toLowerCase();
    const prod        = sub.product || {};
    const mrp         = parseFloat(prod.price        || sub.price        || 0);
    const salePrice   = parseFloat(prod.sale_price   || sub.salePrice    || mrp);
    const subPrice    = parseFloat(prod.subscription_price || sub.subscriptionPrice || salePrice);
    const discount    = sub.plan?.discount || sub.planDiscount || sub.discount || 0;
    const perDelivery = subPrice > 0 ? Math.round(subPrice * (1 - discount / 100)) : 0;
    const freq        = sub.planId || sub.frequency || '';
    const deliveries  = freq === 'daily' ? 30 : freq === 'alternate' ? 15 : 4;
    const totalSaving = mrp > 0 ? Math.round((mrp * deliveries) - (perDelivery * deliveries)) : 0;

    const statusColors = {
      active:    'bg-[#e8f7f3] text-[#03a685]',
      paused:    'bg-yellow-100 text-yellow-700',
      cancelled: 'bg-red-100 text-red-600',
      canceled:  'bg-red-100 text-red-600',
      expired:   'bg-gray-100 text-[#94969f]',
    };
    const statusCls = statusColors[statusLower] || 'bg-gray-100 text-[#94969f]';

    return (
      <div key={sub.id || sub.subscription_number} className="bg-white rounded shadow-sm overflow-hidden">
        {/* Card header — same gradient style as OrdersScreen */}
        <div className="bg-[#e8f7f3] p-4 border-b border-[#b7e5d8]">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-bold text-[#282c3f]">
                {sub.subscription_number || (sub.id ? `Subscription #${sub.id}` : 'Subscription')}
              </h3>
              <p className="text-sm text-[#535766] mt-0.5">
                {sub.plan?.name || sub.planName || sub.frequency || '—'} plan
              </p>
              <p className="text-sm text-[#94969f] mt-0.5 flex items-center gap-1">
                📅 Started: {fmtDate(sub.startDate || sub.created_at)}
              </p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusCls}`}>
              {statusLower.charAt(0).toUpperCase() + statusLower.slice(1)}
            </span>
          </div>
        </div>

        {/* Card body */}
        <div className="p-4">
          {/* Product row */}
          <div
            className="flex items-center gap-3 mb-3 cursor-pointer"
            onClick={() => openDetails(prod)}
            title="View details"
          >
            <div className="flex-shrink-0">{getProductImage(prod, 'large')}</div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-[#282c3f] truncate hover:text-[#ff3f6c]">
                {prod.name || sub.productName || 'Subscription'}
              </p>
              {prod.description && (
                <p className="text-xs text-[#94969f] truncate mt-0.5">{prod.description}</p>
              )}
              {sub.deliverySlot && (
                <p className="text-xs text-[#94969f] mt-0.5">
                  {sub.deliverySlot === 'morning' ? '🌅 Morning (6–8 AM)' : sub.deliverySlot}
                </p>
              )}
            </div>
          </div>

          {/* Pricing table — mirrors order totals */}
          {mrp > 0 && (
            <div className="border border-gray-100 rounded-lg divide-y divide-gray-100 mb-3 text-sm">
              <div className="flex justify-between px-3 py-2">
                <span className="text-[#94969f]">MRP / delivery</span>
                <span className="text-[#94969f] line-through">₹{mrp.toFixed(2)}</span>
              </div>
              {salePrice < mrp && (
                <div className="flex justify-between px-3 py-2">
                  <span className="text-[#94969f]">Sale price / delivery</span>
                  <span className="text-[#535766]">₹{salePrice.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between px-3 py-2">
                <span className="text-[#94969f]">Subscription price</span>
                <span className="font-semibold text-[#ff3f6c]">
                  ₹{(perDelivery > 0 ? perDelivery : Math.round(salePrice)).toFixed(0)} / delivery
                </span>
              </div>
              {totalSaving > 0 && (
                <div className="flex justify-between px-3 py-2 bg-[#e8f7f3] rounded-b-lg">
                  <span className="text-[#03a685] font-medium">
                    Total saving ({deliveries} deliveries)
                  </span>
                  <span className="font-bold text-[#03a685]">₹{totalSaving.toFixed(0)}</span>
                </div>
              )}
            </div>
          )}

          {/* Action buttons — mirrors order actions */}
          <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
            {statusLower === 'cancelled' || statusLower === 'canceled' ? (
              <>
                <button
                  onClick={() => handleReactivateSubscription && handleReactivateSubscription(sub.id)}
                  className="flex-1 text-[#ff3f6c] text-sm font-medium bg-[#fff8fa] px-3 py-2 rounded-lg hover:bg-[#fff8fa] transition-all"
                >
                  Reactivate
                </button>
                <button
                  onClick={() => typeof handleModifySubscription === 'function' && handleModifySubscription(sub)}
                  className="flex-1 text-[#535766] text-sm font-medium bg-gray-50 px-3 py-2 rounded-lg hover:bg-gray-100 transition-all"
                >
                  Modify Plan
                </button>
              </>
            ) : statusLower === 'paused' || statusLower === 'inactive' ? (
              <>
                <button
                  onClick={() => handleResumeSubscription && handleResumeSubscription(sub.id)}
                  className="flex-1 text-[#03a685] text-sm font-medium bg-[#e8f7f3] px-3 py-2 rounded-lg hover:bg-[#e8f7f3] transition-all"
                >
                  Resume
                </button>
                <button
                  onClick={() => handleCancelSubscription && handleCancelSubscription(sub.id)}
                  className="flex-1 text-red-600 text-sm font-medium bg-red-50 px-3 py-2 rounded-lg hover:bg-red-100 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={() => typeof handleModifySubscription === 'function' && handleModifySubscription(sub)}
                  className="flex-1 text-[#535766] text-sm font-medium bg-gray-50 px-3 py-2 rounded-lg hover:bg-gray-100 transition-all"
                >
                  Modify Plan
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => handlePauseSubscription && handlePauseSubscription(sub.id)}
                  className="flex-1 text-[#ff3f6c] text-sm font-medium bg-[#fff8fa] px-3 py-2 rounded-lg hover:bg-[#fff8fa] transition-all"
                >
                  Pause
                </button>
                <button
                  onClick={() => handleCancelSubscription && handleCancelSubscription(sub.id)}
                  className="flex-1 text-red-600 text-sm font-medium bg-red-50 px-3 py-2 rounded-lg hover:bg-red-100 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={() => typeof handleModifySubscription === 'function' && handleModifySubscription(sub)}
                  className="flex-1 text-[#535766] text-sm font-medium bg-gray-50 px-3 py-2 rounded-lg hover:bg-gray-100 transition-all"
                >
                  Modify Plan
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="pb-20 min-h-screen bg-gray-50">
        <div className="fashion-scope bg-white" style={{ borderBottom: '1px solid var(--fx-line)' }}>
          <div className={`flex items-center gap-3 px-4 py-3 ${isDesktop ? 'max-w-[1200px] mx-auto' : ''}`}>
            <button
              onClick={() => goBack ? goBack() : setActiveTab('profile')}
              className="p-1 -ml-1"
              title="Go Back"
              aria-label="Go back"
            >
              <ArrowLeft size={22} style={{ color: 'var(--fx-ink)' }} />
            </button>
            <span className="fx-label">My Subscriptions</span>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center mt-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
          <p className="text-[#94969f]">Loading subscriptions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-20 min-h-screen bg-gray-50">
      {/* Same bar as Shopping Bag on the cart page. */}
      <div className="fashion-scope bg-white" style={{ borderBottom: '1px solid var(--fx-line)' }}>
        <div className={`flex items-center gap-3 px-4 py-3 ${isDesktop ? 'max-w-[1200px] mx-auto' : ''}`}>
          <button
            onClick={() => goBack ? goBack() : setActiveTab('profile')}
            className="p-1 -ml-1"
            title="Go Back"
            aria-label="Go back"
          >
            <ArrowLeft size={22} style={{ color: 'var(--fx-ink)' }} />
          </button>
          <span className="fx-label flex-1">My Subscriptions</span>
          {allSubscriptions.length > 0 && (
            <span style={{ fontSize: 13, color: 'var(--fx-ink-3)' }}>
              {allSubscriptions.length} {allSubscriptions.length === 1 ? 'plan' : 'plans'}
            </span>
          )}
          {loadSubscriptions && (
            <button
              onClick={loadSubscriptions}
              className="p-1 -mr-1"
              title="Refresh"
              aria-label="Refresh subscriptions"
            >
              <RefreshCw size={17} style={{ color: 'var(--fx-ink-2)' }} />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 pt-4 pb-4">
        {allSubscriptions.length === 0 ? (
          <div className="flex flex-col items-center justify-center mt-16 px-6 text-center">
            <Bell size={64} className="text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-[#535766] mb-2">No subscriptions yet</h3>
            <p className="text-[#94969f] mb-6">Subscribe to your favourite products for guaranteed daily deliveries.</p>
            <button
              onClick={() => setActiveTab('home')}
              className="px-6 py-2.5 rounded bg-[#03a685] text-white text-sm font-medium hover:bg-[#028f73] transition-all"
            >
              Browse Products
            </button>
          </div>
        ) : (
          <div className={isDesktop ? 'grid grid-cols-1 lg:grid-cols-2 gap-5 items-start' : 'space-y-4'}>
            {allSubscriptions.map((sub, idx) => (
              <div key={sub.id || sub.subscription_number || idx}>
                {renderCard(sub)}
              </div>
            ))}
          </div>
        )}
      </div>

      <ProductDetailsModal
        cart={cart}
        getCartCount={getCartCount}
        getCartTotal={getCartTotal}
        openCheckout={openCheckout}
        isOpen={showProductDetails}
        onClose={() => { setShowProductDetails(false); setSelectedProduct(null); }}
        product={selectedProduct}
        addToCart={addToCart}
        showToast={showToast}
        favorites={favorites}
        toggleFavorite={toggleFavorite}
        setShowSubscription={setShowSubscription}
        appTheme={appTheme}
      />
    </div>
  );
};

export default SubscriptionsScreen;
